import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	CreateJobGroupClusterBody,
	UpdateJobGroupClusterBody,
} from '@/types'
import {
	createJobGroupCluster,
	deleteJobGroupCluster,
	getJobGroupClusters,
	updateJobGroupCluster,
} from '../services/job-group-cluster'
import { commitmentMatrixKeys } from './commitment-matrix-queries'

// ── Query keys ────────────────────────────────────────────────────────────────

export const jobGroupClusterKeys = {
	all: ['job-group-clusters'] as const,
}

// ── GET /job-group-clusters ───────────────────────────────────────────────────

export function useJobGroupClusters() {
	return useQuery({
		queryKey: jobGroupClusterKeys.all,
		queryFn: () => getJobGroupClusters(),
		staleTime: 10 * 60 * 1000,
		select: (data) => data.items,
	})
}

// ── POST /job-group-clusters ──────────────────────────────────────────────────

export function useCreateJobGroupCluster() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateJobGroupClusterBody) =>
			createJobGroupCluster(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupClusterKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── PATCH /job-group-clusters/:id ─────────────────────────────────────────────

export function useUpdateJobGroupCluster() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateJobGroupClusterBody
		}) => updateJobGroupCluster(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupClusterKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── DELETE /job-group-clusters/:id ────────────────────────────────────────────

export function useDeleteJobGroupCluster() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteJobGroupCluster(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: jobGroupClusterKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}
